// The rebuild of v3's three untypeable getters (`integrationStore.ts:139-208`).
//
// v3's `getAppIntegration`/`getShopIntegration` returned `string | object | boolean | null`
// depending on an optional `type` argument, and instantiated `useWorkspaceStore()` AND
// `useDashboardStore()` *inside a Pinia getter* to read
// `workspaceStore.wsIntegrations[dashboardStore.dashboardWsId]`. Two stores reached from inside a
// third store's getter is why nothing above it could be typed.
//
// Everything here is a pure function of an integration map the caller already holds. No store, no
// Vue reactivity, no `this`. The exclusive-group knowledge that was hardcoded as
// `{adjust, appsflyer}` / `{shopify, ticimax, tsoft}` object literals now comes from the registry's
// `exclusiveGroup`/`exclusiveMember`, so adding a fourth e-commerce connector is a registry edit.

import { resolveChannelKey } from '../models/channelAliases'
import type { IntegrationAccountDto, WorkspaceIntegrationMap } from '../models/integration.types'
import { INTEGRATION_PROVIDERS, providersById } from '../models/providers'
import type { IntegrationProvider } from '../models/providers.types'

/**
 * The linked accounts for a channel, addressed by alias or canonical key.
 *
 * v3 returned `true` (not an array) when the channel was absent from the map but present in the
 * dashboard workspace's `wsIntegrations` list — a third return type that callers had to guard.
 * That fallback is gone: callers that need another workspace's map pass that map in.
 */
export function accountsForChannel(
  integrations: WorkspaceIntegrationMap,
  channel: string,
): IntegrationAccountDto[] {
  return integrations[resolveChannelKey(channel)] ?? []
}

/**
 * v3 `format.js` record predicates (ORP-24065/24076). A row stops being read once its grant is gone:
 * `status === 'inactive'` marks one way, a backend-computed `needs_reauth` another. Both are
 * UNREADABLE — kept out of the label and out of "connected" — but reported apart, because a reauth
 * row asks for a reconnect while an inactive row reads as a lost connection.
 */
export function isInactiveRecord(account: IntegrationAccountDto): boolean {
  return account.status === 'inactive'
}

export function isReauthRecord(account: IntegrationAccountDto): boolean {
  return account.needs_reauth === true
}

export function isUnreadableRecord(account: IntegrationAccountDto): boolean {
  return isInactiveRecord(account) || isReauthRecord(account)
}

/** A BigQuery export row names its variant on `integration_config.type`; older rows on the row. */
function exportTypeOf(account: IntegrationAccountDto): unknown {
  return account.integration_config?.type ?? account.type ?? null
}

/**
 * The records ONE provider reads — v3 `getIntegrationRecords` (`integrationRecords.js:16-21`), the
 * single answer the section, the label, the state and onboarding all share.
 *
 *   · a BigQuery channel export (GA4, Adapty, Play Store) reads `bigquery_table` AND
 *     `bigquery_account` rows, kept to its own `exportType` (ORP-25670);
 *   · BigQuery Custom — the shared-id variant with no `exportType` — reads nothing, so it never
 *     lands under Connected and always offers Connect;
 *   · everything else reads its own channel.
 */
export function providerRecords(
  integrations: WorkspaceIntegrationMap,
  provider: IntegrationProvider,
): IntegrationAccountDto[] {
  if (provider.exportType !== undefined) {
    return [
      ...accountsForChannel(integrations, 'bigquery_table'),
      ...accountsForChannel(integrations, provider.id),
    ].filter((account) => exportTypeOf(account) === provider.exportType)
  }
  if (providersById(provider.id).length > 1) return []
  return accountsForChannel(integrations, provider.id)
}

/** v3 `isIntegrationConnected`: at least one READABLE record for the provider. */
export function isProviderConnected(
  integrations: WorkspaceIntegrationMap,
  provider: IntegrationProvider,
): boolean {
  return providerRecords(integrations, provider).some((account) => !isUnreadableRecord(account))
}

/** Whether a channel has at least one linked account. v3's `getIntegrationByName(name)` truthiness. */
export function hasChannel(integrations: WorkspaceIntegrationMap, channel: string): boolean {
  return accountsForChannel(integrations, channel).length > 0
}

/**
 * The connected member of a mutually-exclusive group, or `null`.
 *
 * Replaces `getAppIntegration()`/`getShopIntegration()` with one selector over the registry.
 * Registry order decides ties, which reproduces v3's hardcoded precedence: adjust before appsflyer,
 * shopify before ticimax before tsoft.
 *
 * A workspace *can* currently hold two members at once — v3 never enforced the rule on write — so
 * this reports the first, it does not assert uniqueness. Fixing that is a product decision with
 * live data behind it (analysis §7.3).
 */
export function exclusiveMember(
  integrations: WorkspaceIntegrationMap,
  group: 'shop' | 'app',
): IntegrationProvider | null {
  return (
    INTEGRATION_PROVIDERS.find(
      (provider) => provider.exclusiveGroup === group && hasChannel(integrations, provider.id),
    ) ?? null
  )
}

/** Every provider with at least one linked account in this map. */
export function connectedProviders(
  integrations: WorkspaceIntegrationMap,
): readonly IntegrationProvider[] {
  return INTEGRATION_PROVIDERS.filter((provider) => isProviderConnected(integrations, provider))
}
